import { type CSSProperties, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import Card from '../Card';
import { allStages, stages } from '../../config';
import { formatAegStage, AEG_NEXT_STAGES, type Request, type StageKey } from '../../types';
import './styles.css';

type BoardProps = {
    rows: Request[];
    selectedStage: StageKey | null;
    onMove: (id: string, stage: StageKey) => void;
    onSelect: (id: string) => void;
};

export default function Board({ rows, selectedStage, onMove, onSelect }: BoardProps) {
    const intl = useIntl();
    const visibleStages = selectedStage ? [selectedStage] : allStages;
    const [dropStage, setDropStage] = useState<StageKey | null>(null);
    const [draggedId, setDraggedId] = useState<string | null>(null);
    const draggedStage = rows.find(row => row.id === draggedId)?.stage;
    const allowedStages = draggedStage ? AEG_NEXT_STAGES[draggedStage] : [];

    useEffect(() => {
        const clearDrop = () => {
            setDropStage(null);
            setDraggedId(null);
        };
        window.addEventListener('dragend', clearDrop);
        window.addEventListener('drop', clearDrop);
        return () => {
            window.removeEventListener('dragend', clearDrop);
            window.removeEventListener('drop', clearDrop);
        };
    }, []);

    return (
        <div className='aeg-board-root'>
            {visibleStages.map(stage => {
                const items = rows.filter(row => row.stage === stage);
                const isDropTarget = dropStage === stage && allowedStages.includes(stage);

                return (
                    <section
                        className='aeg-board-column bg-lightest'
                        key={stage}
                        onDragEnter={event => {
                            event.preventDefault();
                            setDropStage(stage);
                        }}
                        onDragOver={event => {
                            event.preventDefault();
                            setDropStage(current => (current === stage ? current : stage));
                        }}
                        onDragLeave={event => {
                            const next = event.relatedTarget;
                            if (next instanceof Node && event.currentTarget.contains(next)) {
                                return;
                            }
                            setDropStage(current => (current === stage ? null : current));
                        }}
                        onDrop={event => {
                            setDropStage(null);
                            setDraggedId(null);
                            onMove(event.dataTransfer.getData('requestId'), stage);
                        }}
                        style={{ '--stage-color': stages[stage].color } as CSSProperties}
                    >
                        <h3 className='display-flex justify-content-between margin-0 margin-bottom-10 text-bold text-color-darkest'>
                            <span>{formatAegStage(intl, stage)}</span>
                            <span>{items.length}</span>
                        </h3>
                        <div className={`aeg-board-column-body${isDropTarget ? ' is-drop-target' : ''}`}>
                            {items.map(request => (
                                <Card
                                    key={request.id}
                                    onDragStart={setDraggedId}
                                    onSelect={onSelect}
                                    request={request}
                                />
                            ))}
                            {items.length === 0 ? (
                                <div className='aeg-board-empty padding-30 text-center text-color-dark'>
                                    {intl.formatMessage({ id: 'intl-msg:support.aeg.board.empty' })}
                                </div>
                            ) : null}
                        </div>
                    </section>
                );
            })}
        </div>
    );
}
